import { Card } from "@mui/material";
import { useParams } from "@reach/router";
import { useProject } from "../../hooks/useProject";
import { useTeam } from "../../hooks/useTeam";
import { useDesign } from "../../hooks/useDesign";
import { sumNumbers } from "./Projects";
import Quote, { A4Estimate, Extras } from "./Quote";
import { getSections } from "../../util/fence";
import { api } from "../../util/api";
import { useEffect, useState, useRef } from "react";
import { TextFieldTWSolo } from "../../comp/tw/TextField";
import { ButtonTW } from "../../comp/tw/Button";
import SaveIcon from "@mui/icons-material/Save";
import { enqueueSnackbar } from "notistack";
import Text from "../../comp/tw/Text";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export const ProjectNumbers = () => {
  const { projectId } = useParams();
  const { teamIsLoading, team, error: teamError } = useTeam();
  const {
    project,
    isLoading: projectIsLoading,
    error: projectError,
  } = useProject(projectId);
  const { design, isLoading, error } = useDesign(project.designId);

  if (isLoading || teamIsLoading || projectIsLoading) {
    return <div className="spinner" />;
  }

  if (error?.response?.data === "Design is not in team") {
    return null;
  }

  if (error || teamError || projectError) {
    return (
      <p>
        Error! please refresh the page, if the problem persists contact
        fencewizapp@gmail.com
      </p>
    );
  }

  if (!team.permissions.includes("seeQuote")) {
    return null;
  }

  if (!project.lengths && team.permissions.includes("editProject")) {
    return <p>Please enter the length of the fence</p>;
  } else if (!project.lengths && !team.permissions.includes("editProject")) {
    return <p>Have an owner or a quoter add the length to the fence</p>;
  }
  if (
    (!team.team.labourCostPerFt ||
      !team.team.serviceTax ||
      !team.team.materialTax ||
      !team.team.materialMultiplier) &&
    team.tierLimits.seePrices &&
    !team.permissions.includes("editTeam")
  ) {
    return (
      <p className="text-xl font-semibold">
        Have an owner enter values in Team Page
      </p>
    );
  }

  if (
    (!team.team.labourCostPerFt ||
      !team.team.serviceTax ||
      !team.team.materialTax ||
      !team.team.materialMultiplier) &&
    team.tierLimits.seePrices &&
    team.permissions.includes("editTeam")
  ) {
    return <p className="text-xl font-semibold">Enter values in Team Page</p>;
  }

  if (!team.tierLimits.seePrices) {
    return (
      <Card>
        <div>
          <p>Please Subscribe to a Higher Tier to see prices</p>
        </div>
      </Card>
    );
  }

  let numberOfPanelsPerLengthCalc = Object.values(project.lengths).map((s) =>
    getSections(parseFloat(s), parseFloat(design.lengthOfSection || 1)),
  );

  const totalSections = numberOfPanelsPerLengthCalc.reduce(
    (partialSum, a) => partialSum + a,
    0,
  );

  let totalPosts = numberOfPanelsPerLengthCalc.reduce(
    (partialSum, a) => partialSum + a + 1,
    0,
  );

  totalPosts = totalPosts - (project?.sharedPosts || 0);

  const totalCalc = () => {
    let total = 0;
    design.materials.forEach((m) => {
      if (m.fenceCategory === "post") {
        total += m.price * totalPosts * m.quantity;
      } else {
        total += m.price * totalSections * m.quantity;
      }
    });
    return total;
  };

  const total = totalCalc();
  const plusTax = (total * parseFloat(team.team.materialTax / 100 + 1)).toFixed(
    2,
  );

  const adjustCost = parseFloat(project.adjustCost) || 0;

  // console.log(adjustCost);
  const materialMultiplier = (
    plusTax * parseFloat(team.team.materialMultiplier / 100 + 1)
  ).toFixed(2);

  const totalLabour = (
    team?.team?.labourCostPerFt * sumNumbers(Object.values(project.lengths))
  ).toFixed(2);

  const totalCost = (
    parseFloat(totalLabour) + parseFloat(materialMultiplier)
  ).toFixed(2);

  const totalClientCost = (
    totalCost * parseFloat(team.team.serviceTax / 100 + 1)
  ).toFixed(2);

  const pricePerFtBeforeTax = (
    adjustCost +
    totalCost / sumNumbers(Object.values(project.lengths))
  ).toFixed(2);

  const costAdjustment = (
    adjustCost * sumNumbers(Object.values(project.lengths))
  ).toFixed(2);

  // const totalClientCostPerFt = (
  //   adjustCost +
  //   totalClientCost / sumNumbers(Object.values(project.lengths))
  // ).toFixed(2);

  return (
    <>
      <div>
        {team.permissions.includes("seeNumbers") ? (
          <>
            <p>
              Material Markup: ${(totalCost - totalLabour - plusTax).toFixed(2)}
            </p>
            <p>Material Plus Markup: ${materialMultiplier}</p>
            <p>Labour Cost: ${totalLabour}</p>
            <p>Total Cost: ${totalCost}</p>{" "}
            <p>Cost Adjustment: ${costAdjustment}</p>
          </>
        ) : null}
        <p>
          <b>Customer Cost including Tax: ${totalClientCost}</b>
        </p>
        <p>${pricePerFtBeforeTax} plus tax per ft</p>
        {/* <p>${totalClientCostPerFt} per ft </p> */}
      </div>

      <Quote totalCost={totalCost} pricePerFtBeforeTax={pricePerFtBeforeTax} />
      <Splits pricePerFtBeforeTax={pricePerFtBeforeTax} />
    </>
  );
};
const Splits = ({ pricePerFtBeforeTax }) => {
  const { projectId } = useParams();
  const { project, mutate } = useProject(projectId);
  const allEstimatesRef = useRef();

  const [splits, setSplits] = useState(project.splits || {});

  const handleSplitChange = async (run, checked) => {
    const newSplits = { ...splits };
    if (checked) {
      newSplits[run] = { name: "", extras: [] };
    } else {
      newSplits[run] = null;
    }
    setSplits(newSplits);
    // Update project with new splits
    await api("PUT", `/project/${projectId}`, { splits: newSplits });
    mutate(); // Refetch project data to trigger updates
  };

  const generateCombinedPDF = async () => {
    enqueueSnackbar("Generating combined PDF...");

    const input = allEstimatesRef.current;

    if (!input) {
      enqueueSnackbar("Error: Content not ready", { variant: "error" });
      return;
    }

    try {
      // Wait a bit for images to load
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Make container visible for rendering
      input.style.position = "absolute";
      input.style.left = "-9999px";
      input.style.visibility = "visible";
      input.style.display = "block";
      input.style.height = "auto";
      input.style.overflow = "visible";

      // Get all individual estimate divs
      const estimateDivs = input.querySelectorAll(".estimate-page");

      if (estimateDivs.length === 0) {
        enqueueSnackbar("No estimates found", { variant: "error" });
        input.style.visibility = "hidden";
        return;
      }

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Process each estimate separately
      for (let i = 0; i < estimateDivs.length; i++) {
        const estimateDiv = estimateDivs[i];

        // Capture this specific estimate
        const canvas = await html2canvas(estimateDiv, {
          scale: 1.5, // Reduced from 2 for smaller file size
          useCORS: true,
          logging: false,
          allowTaint: false,
          backgroundColor: "#ffffff",
          imageTimeout: 15000,
          removeContainer: false,
        });

        const imgData = canvas.toDataURL("image/jpeg", 0.75); // Use JPEG with 75% quality instead of PNG
        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * pageWidth) / canvas.width;

        // Add new page for each estimate (except the first one)
        if (i > 0) {
          pdf.addPage();
        }

        // If the content is taller than one page, split it
        if (imgHeight > pageHeight) {
          let heightLeft = imgHeight;
          let position = 0;

          // Add first page
          pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;

          // Add additional pages if needed
          while (heightLeft > 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
          }
        } else {
          // Fits on one page
          pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight);
        }
      }

      // Hide container again
      input.style.visibility = "hidden";
      input.style.position = "fixed";
      input.style.left = "-10000px";
      input.style.display = "block";
      input.style.height = "0";
      input.style.overflow = "hidden";

      pdf.save(
        `${project.clientFirstName} ${project.clientLastName} - All Estimates.pdf`,
      );
      enqueueSnackbar("PDF downloaded successfully!", { variant: "success" });
    } catch (error) {
      console.error("Error generating PDF:", error);
      enqueueSnackbar("Error generating PDF: " + error.message, {
        variant: "error",
      });

      // Make sure to hide container on error
      if (input) {
        input.style.visibility = "hidden";
        input.style.position = "fixed";
        input.style.left = "-10000px";
        input.style.display = "block";
        input.style.height = "0";
        input.style.overflow = "hidden";
      }
    }
  };

  return (
    <div className="mt-8">
      <Text text="Splits" level={3} />

      <div className="mb-4">
        <ButtonTW
          label="Download All Quotes as Single PDF"
          onClick={generateCombinedPDF}
          variant="primary"
        />
      </div>

      {/* Hidden container for PDF generation */}
      <div
        ref={allEstimatesRef}
        style={{
          position: "fixed",
          top: 0,
          left: "-10000px",
          visibility: "hidden",
          width: "210mm",
          height: "0",
          overflow: "hidden",
          pointerEvents: "none",
          zIndex: -9999,
        }}
      >
        <AllEstimatesContainer
          project={project}
          splits={splits}
          pricePerFtBeforeTax={pricePerFtBeforeTax}
        />
      </div>

      {Object.entries(project.lengths).map(([run, length], idx) => (
        <div
          key={`${idx}-length`}
          className="mb-8 p-4 border border-gray-300 rounded-lg"
        >
          <div className="flex items-center gap-4 mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={splits[run] !== null && splits[run] !== undefined}
                onChange={(e) => handleSplitChange(run, e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
              <span className="font-semibold text-lg">
                Run {parseInt(run) + 1}: {length} ft
              </span>
            </label>
          </div>
          {splits[run] !== null && splits[run] !== undefined ? (
            <Split run={run} pricePerFtBeforeTax={pricePerFtBeforeTax} />
          ) : (
            <p className="text-gray-500 italic ml-7">Split Inactive</p>
          )}
        </div>
      ))}
      <NewInvoice pricePerFtBeforeTax={pricePerFtBeforeTax} />
    </div>
  );
};

const AllEstimatesContainer = ({ project, splits, pricePerFtBeforeTax }) => {
  const { team } = useTeam();
  const { design } = useDesign(project.designId);
  const [imageData, setImageData] = useState(null);
  const [designImage, setDesignImage] = useState(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  useEffect(() => {
    const fetchImage = async (url) => {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = () => {
            resolve(reader.result);
          };
        });
      } catch (error) {
        console.error("Error fetching image:", error);
        return null;
      }
    };

    const loadImages = async () => {
      const promises = [];

      if (team.team.logo) {
        promises.push(
          fetchImage(team.team.logo).then((data) => setImageData(data)),
        );
      }

      if (design.image) {
        promises.push(
          fetchImage(design.image).then((data) => setDesignImage(data)),
        );
      }

      await Promise.all(promises);
      setImagesLoaded(true);
    };

    loadImages();
  }, [team.team.logo, design.image]);

  const renderEstimate = (clientName, lineItems) => {
    const subtotal = sumNumbers(
      lineItems.map(
        (item) => parseFloat(item.price) * parseFloat(item.quantity),
      ),
    );
    const tax = subtotal * (team.team.serviceTax / 100 + 1) - subtotal;

    return (
      <div className="estimate-page bg-white p-5 md:p-10 mx-auto my-10 border border-gray-300 shadow-md max-w-[210mm] text-gray-800">
        {/* Header */}
        <header className="mb-10">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold">Quote</h1>
            {imageData && <img src={imageData} alt="logo" className="w-24" />}
          </div>

          <p className="text-lg">{team.team.name}</p>
          {clientName ? (
            <p className="text-sm">Prepared For: {clientName}</p>
          ) : null}

          <p className="text-sm">{project.address}</p>
          {project.city && project.state ? (
            <p className="text-sm">
              {project.city}, {project.state}
            </p>
          ) : null}

          <p className="text-sm">Date: {new Date().toDateString()}</p>
        </header>

        {/* Project Details */}
        {project.notes && (
          <section className="mb-10">
            <h2 className="text-xl font-semibold mb-2">Project Details</h2>
            <p>{project.notes}</p>
          </section>
        )}

        {designImage ? (
          <img src={designImage} className="h-60" alt="designImage" />
        ) : null}

        {/* Line Items Table */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-2">Line Items</h2>
          <div className="grid grid-cols-5 text-sm">
            <p className="border-y-4 p-2 text-left col-span-2">Item</p>
            <p className="border-y-4 p-2 text-left">Quantity</p>
            <p className="border-y-4 p-2 text-left">Price</p>
            <p className="border-y-4 p-2 text-right">Total</p>

            {lineItems &&
              lineItems.map((item, idx) => (
                <div key={`${idx}-line-item`} className="contents">
                  <p className="p-2 col-span-2">{item.item}</p>
                  <p className="p-2">{item.ft ? item.ft : item.quantity}</p>
                  <p className="p-2 text-left">
                    ${parseFloat(item.price).toFixed(2)}
                  </p>
                  <p className="p-2 text-right">
                    $
                    {(
                      parseFloat(item.price) * parseFloat(item.quantity)
                    ).toFixed(2)}
                  </p>
                </div>
              ))}
          </div>
        </section>

        {/* Totals */}
        <section className="mt-10">
          <div className="flex justify-between font-semibold text-lg">
            <span>Subtotal:</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span>Tax:</span>
            <span>${tax.toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-bold text-xl mt-4">
            <span>Total:</span>
            <span>${(subtotal + tax).toFixed(2)}</span>
          </div>
        </section>

        {/* Footer */}
        <footer className="mt-20 text-sm text-gray-600">
          <p>Thank you for your business!</p>
          <p>Please contact us with any questions regarding this estimate.</p>
        </footer>
      </div>
    );
  };

  let totalNonSplit = 0;
  let totalSplit = 0;

  Object.entries(project.lengths).forEach(([run, length]) => {
    if (!splits || !splits[run]) {
      totalNonSplit += parseFloat(length);
    } else {
      totalSplit += parseFloat(length);
    }
  });

  if (!imagesLoaded) {
    return <div>Loading images...</div>;
  }

  return (
    <div>
      {/* Render split estimates */}
      {Object.entries(project.lengths).map(([run, length]) => {
        if (splits[run] !== null && splits[run] !== undefined) {
          const lineItems = [
            {
              item: design?.name || "Item",
              ft: `${length}ft`,
              quantity: parseFloat(length) || 0,
              price: parseFloat(pricePerFtBeforeTax) / 2 || 0,
            },
            ...(splits[run]?.extras || []),
          ];
          return (
            <div key={`estimate-${run}`}>
              {renderEstimate(splits[run]?.name || "", lineItems)}
            </div>
          );
        }
        return null;
      })}

      {/* Render combined split invoice if there are splits */}
      {totalSplit !== 0 && (
        <div>
          {renderEstimate(
            `${project.clientFirstName} ${project.clientLastName} - Split Invoice`,
            [
              {
                item: `${design.name} - Split`,
                ft: `${totalSplit}ft`,
                quantity: totalSplit,
                price: pricePerFtBeforeTax / 2,
              },
              ...(totalNonSplit !== 0
                ? [
                    {
                      item: design.name,
                      ft: `${totalNonSplit}ft`,
                      quantity: totalNonSplit,
                      price: pricePerFtBeforeTax,
                    },
                  ]
                : []),
              ...(project?.extras || []),
            ],
          )}
        </div>
      )}

      {/* Render main estimate if no splits */}
      {totalSplit === 0 && (
        <div>
          {renderEstimate(
            `${project.clientFirstName} ${project.clientLastName}`,
            [
              {
                item: design.name,
                ft: `${totalNonSplit}ft`,
                quantity: totalNonSplit,
                price: pricePerFtBeforeTax,
              },
              ...(project?.extras || []),
            ],
          )}
        </div>
      )}
    </div>
  );
};

const Split = ({ run, pricePerFtBeforeTax }) => {
  const { projectId } = useParams();
  const { team } = useTeam();
  const { project, mutate } = useProject(projectId);
  const { design } = useDesign(project.designId);
  const [splitName, setSplitName] = useState(
    project?.splits?.[run]?.name || "",
  );

  const handleNameChange = (e) => {
    setSplitName(e.target.value);
  };

  const addNameToSplit = async (run) => {
    console.log("Adding name to split:", splitName);
    const newSplits = {
      ...project.splits,
      [run]: { ...project.splits[run], name: splitName },
    };
    await api("PUT", `/project/${projectId}`, { splits: newSplits });
    enqueueSnackbar("Split name updated", { variant: "success" });
    mutate(); // Refetch project data to trigger updates
  };

  return (
    <div className="ml-7 space-y-4">
      <div className="flex gap-2 items-center">
        <TextFieldTWSolo
          label="Name for Split Invoice"
          value={splitName}
          onChange={handleNameChange}
        />
        <ButtonTW label={<SaveIcon />} onClick={() => addNameToSplit(run)} />
      </div>

      <Extras where={run} />
      {project?.lengths?.[run] && (
        <A4Estimate
          key={`${projectId}-estimate-${run}`}
          project={project}
          clientName={project?.splits?.[run]?.name || ""}
          address={project?.address || ""}
          city={project?.city || ""}
          state={project?.state || ""}
          notes={project?.notes || ""}
          lineItems={[
            {
              item: design?.name || "Item",
              ft: `${project.lengths[run]}ft`,
              quantity: parseFloat(project.lengths[run]) || 0,
              price: parseFloat(pricePerFtBeforeTax) / 2 || 0,
            },
            ...(project?.splits?.[run]?.extras || []),
          ]}
          team={team}
          design={design}
        />
      )}
    </div>
  );
};

const NewInvoice = ({ pricePerFtBeforeTax }) => {
  const { projectId } = useParams();
  const { team } = useTeam();
  const { project } = useProject(projectId);
  const { design } = useDesign(project.designId);

  const [totalNonSplit, setTotalNonSplit] = useState(0);
  const [totalSplit, setTotalSplit] = useState(0);

  useEffect(() => {
    let nonSplit = 0;
    let split = 0;

    Object.entries(project.lengths).forEach(([run, length]) => {
      if (!project.splits || !project.splits[run]) {
        nonSplit += parseFloat(length);
      } else {
        split += parseFloat(length);
      }
    });

    setTotalNonSplit(nonSplit);
    setTotalSplit(split);
  }, [project.lengths, project.splits]);

  // Build line items before return
  const lineItems = [];

  // Add split item if totalSplit is not 0
  if (totalSplit !== 0) {
    lineItems.push({
      item: `${design.name} - Split`,
      ft: `${totalSplit}ft`,
      quantity: totalSplit,
      price: pricePerFtBeforeTax / 2,
    });
  }

  // Add non-split item only if totalNonSplit is not 0
  if (totalNonSplit !== 0) {
    lineItems.push({
      item: design.name,
      ft: `${totalNonSplit}ft`,
      quantity: totalNonSplit,
      price: pricePerFtBeforeTax,
    });
  }

  // Add extras
  lineItems.push(...(project?.extras || []));

  return (
    <div>
      {totalSplit !== 0 && (
        <div>
          <A4Estimate
            key={`${projectId}-estimate-new`}
            project={project}
            clientName={`${project.clientFirstName} ${project.clientLastName} - Split Invoice`}
            address={project.address}
            city={project.city}
            state={project.state}
            notes={project.notes}
            lineItems={lineItems}
            team={team}
            design={design}
          />
        </div>
      )}
    </div>
  );
};
